import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import PageHeader from '../components/PageHeader';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const METHOD_LABELS = { stripe:'💳 Card', cash:'💵 Cash', bank:'🏦 Bank Transfer', online:'🌐 Online', viva:'🔵 Viva', paypal:'🅿️ PayPal' };
const STATUS_COLORS = { active:'var(--gr)', overdue:'var(--ac)', booked:'var(--bl)', complete:'var(--tx3)', cancelled:'var(--tx3)' };
const STATUS_LABELS = { active:'Active', overdue:'Overdue', booked:'Booked', complete:'Complete', cancelled:'Cancelled' };
// Plain-text (no emoji) versions for exports
const METHOD_TXT = { stripe:'Card (Stripe)', cash:'Cash', bank:'Bank Transfer', online:'Online', viva:'Viva', paypal:'PayPal' };
const STATUS_TXT = { active:'Active', overdue:'Overdue', booked:'Booked', complete:'Complete', cancelled:'Cancelled' };

function getRange(key, custom) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  if (key === 'this-month')  return { from: new Date(y, m, 1),      to: new Date(y, m+1, 0, 23, 59, 59) };
  if (key === 'last-month')  return { from: new Date(y, m-1, 1),    to: new Date(y, m, 0, 23, 59, 59) };
  if (key === 'this-year')   return { from: new Date(y, 0, 1),      to: new Date(y, 11, 31, 23, 59, 59) };
  if (key === 'custom' && custom.from && custom.to) return { from: new Date(custom.from), to: new Date(custom.to + 'T23:59:59') };
  return null;
}

function inRange(r, range) {
  if (!range) return true;
  const d = r.start ? new Date(r.start) : null;
  if (!d) return false;
  return d >= range.from && d <= range.to;
}

function SortTh({ label, sortKey, current, onSort }) {
  const active = current.key === sortKey;
  return (
    <div className="th" style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 3 }}
      onClick={() => onSort(sortKey)}>
      {label}
      <span style={{ fontSize: 9, color: active ? 'var(--ac)' : 'var(--bd)', marginLeft: 2 }}>
        {active ? (current.dir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </div>
  );
}

const csvEsc = (val) => {
  const s = (val === null || val === undefined) ? '' : String(val);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function exportRevenueCSV(rows, vehicles, slug) {
  const headers = ['Contract','Vehicle','Plate','Customer','Phone','Start','End','Days','Amount (EUR)','Method','Status'];
  const lines = rows.map(r => {
    const v = vehicles.find(x => x.id === r.vid);
    return [
      r.id, v?.name || '', v?.plate || '', r.customer || '', r.phone || '',
      r.startLabel || '', r.endLabel || '', r.calSpan || '',
      (parseFloat(r.agreedPrice) || 0).toFixed(2),
      METHOD_TXT[r.payMethod] || r.payMethod || '',
      STATUS_TXT[r.status] || r.status || '',
    ].map(csvEsc).join(',');
  });
  // UTF-8 BOM so Excel renders € / accented names; CRLF endings
  const csv = '﻿' + [headers.join(','), ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = `revenue_${slug}.csv`; a.click(); URL.revokeObjectURL(a.href);
}

// Full financial report → print → Save as PDF (no extra deps)
function printRevenuePDF({ rows, vehicles, byVehicle, byMethod, stats, cs, periodLabel }) {
  const esc = (val) => String(val ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const eur = (n) => '€' + (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const co = cs?.name || 'Your Company';
  const logoHtml = cs?.logoDataUrl
    ? `<img src="${cs.logoDataUrl}" style="max-height:60px;max-width:160px;object-fit:contain;"/>`
    : `<div style="font-size:20px;font-weight:800;">${esc(co)}</div>`;
  const genDate = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const txRows = rows.map(r => {
    const v = vehicles.find(x => x.id === r.vid);
    return `<tr>
      <td>${esc(r.id)}</td>
      <td>${esc(v?.name?.split('–')[0]?.trim() || '')}</td>
      <td>${esc(r.customer || '')}</td>
      <td>${esc(r.startLabel || '')}${r.endLabel ? ` → ${esc(r.endLabel)}` : ''}</td>
      <td class="num">${esc(r.calSpan || '')}</td>
      <td class="num">${eur(r.agreedPrice || 0)}</td>
      <td>${esc(METHOD_TXT[r.payMethod] || r.payMethod || '')}</td>
      <td>${esc(STATUS_TXT[r.status] || r.status || '')}</td>
    </tr>`;
  }).join('');

  const vehRows = byVehicle.map(v => `<tr>
    <td>${esc(v.name?.split('–')[0]?.trim() || '')}${v.plate ? `<br/><span class="sub">${esc(v.plate)}</span>` : ''}</td>
    <td class="num">${v.count}</td>
    <td class="num">${v.days}</td>
    <td class="num">${eur(v.total)}</td>
    <td class="num">${eur(v.avg)}</td>
    <td class="num">${v.occ}%</td>
  </tr>`).join('');

  const methodRows = byMethod.map(([m, total]) => {
    const pct = Math.round((total / (stats.totalRevenue || 1)) * 100);
    return `<tr><td>${esc(METHOD_TXT[m] || m)}</td><td class="num">${eur(total)}</td><td class="num">${pct}%</td></tr>`;
  }).join('');

  const statBox = (lb, vl, color) => `<div class="stat"><div class="lb">${esc(lb)}</div><div class="vl"${color ? ` style="color:${color}"` : ''}>${vl}</div></div>`;

  const w = window.open('', '_blank');
  if (!w) return null;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Revenue Report</title>
  <style>
    @page { size: A4 landscape; margin: 12mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color:#222; font-size:11px; margin:0; padding:22px; }
    .hd { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #333; padding-bottom:12px; margin-bottom:14px; }
    .hd .co { text-align:right; font-size:10px; color:#555; line-height:1.5; }
    h1 { font-size:18px; margin:8px 0 2px; }
    h2 { font-size:12px; margin:18px 0 8px; text-transform:uppercase; letter-spacing:.04em; color:#444; border-bottom:1px solid #ddd; padding-bottom:4px; }
    .meta { font-size:11px; color:#666; margin-bottom:12px; }
    .stats { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:6px; }
    .stat { border:1px solid #ddd; border-radius:6px; padding:8px 12px; }
    .stat .lb { font-size:9px; text-transform:uppercase; letter-spacing:.05em; color:#888; }
    .stat .vl { font-size:15px; font-weight:700; }
    table { width:100%; border-collapse:collapse; margin-bottom:4px; }
    th { background:#333; color:#fff; font-size:9px; text-transform:uppercase; letter-spacing:.03em; padding:6px 8px; text-align:left; }
    th.num { text-align:right; }
    td { padding:5px 8px; border-bottom:1px solid #eee; vertical-align:top; }
    td.num { text-align:right; font-variant-numeric: tabular-nums; }
    .sub { color:#999; font-size:9px; }
    tr:nth-child(even) td { background:#fafafa; }
    tfoot td { border-top:2px solid #333; font-weight:700; background:#fff !important; }
    .cols { display:grid; grid-template-columns:1.4fr 1fr; gap:18px; align-items:start; }
    .ft { margin-top:16px; font-size:9px; color:#999; text-align:center; }
  </style></head><body>
    <div class="hd">
      <div>${logoHtml}</div>
      <div class="co">
        ${cs?.vatNum ? `V.A.T ${esc(cs.vatNum)}<br/>` : ''}
        ${cs?.address ? `${esc(cs.address)}<br/>` : ''}
        ${cs?.phone ? `${esc(cs.phone)}<br/>` : ''}
        ${cs?.email ? esc(cs.email) : ''}
      </div>
    </div>
    <h1>Revenue Report</h1>
    <div class="meta">${esc(periodLabel)} · ${rows.length} transaction${rows.length === 1 ? '' : 's'} · Generated ${esc(genDate)}</div>
    <div class="stats">
      ${statBox('Total Revenue', eur(stats.totalRevenue), '#1e7d34')}
      ${statBox('Collected', eur(stats.collected))}
      ${statBox('Outstanding', eur(stats.outstanding), stats.outstanding > 0 ? '#b8860b' : '#1e7d34')}
      ${statBox('Net Profit', eur(stats.netProfit), stats.netProfit >= 0 ? '#1e7d34' : '#c0392b')}
      ${statBox('Avg / Rental', eur(stats.avgRental))}
      ${statBox('Days Rented', stats.totalDaysRented)}
      ${statBox('Maintenance', '−' + eur(stats.maintCost), '#b8860b')}
      ${statBox('Rentals', rows.length)}
    </div>
    <div class="cols">
      <div>
        <h2>Revenue by Vehicle</h2>
        <table>
          <thead><tr><th>Vehicle</th><th class="num">Rentals</th><th class="num">Days</th><th class="num">Revenue</th><th class="num">Avg/Rental</th><th class="num">Occ.</th></tr></thead>
          <tbody>${vehRows || '<tr><td colspan="6" style="text-align:center;padding:14px;color:#999;">No data</td></tr>'}</tbody>
        </table>
      </div>
      <div>
        <h2>Payment Methods</h2>
        <table>
          <thead><tr><th>Method</th><th class="num">Amount</th><th class="num">Share</th></tr></thead>
          <tbody>${methodRows || '<tr><td colspan="3" style="text-align:center;padding:14px;color:#999;">No data</td></tr>'}</tbody>
        </table>
      </div>
    </div>
    <h2>Transactions</h2>
    <table>
      <thead><tr><th>Contract</th><th>Vehicle</th><th>Customer</th><th>Period</th><th class="num">Days</th><th class="num">Amount</th><th>Method</th><th>Status</th></tr></thead>
      <tbody>${txRows || '<tr><td colspan="8" style="text-align:center;padding:14px;color:#999;">No transactions</td></tr>'}</tbody>
      <tfoot><tr><td colspan="5">Total — ${rows.length} transaction${rows.length === 1 ? '' : 's'}</td><td class="num">${eur(stats.totalRevenue)}</td><td colspan="2"></td></tr></tfoot>
    </table>
    <div class="ft">${esc(co)} — Revenue Report — use your browser's “Save as PDF” in the print dialog</div>
    <script>window.onload=function(){setTimeout(function(){window.print();},250);}</script>
  </body></html>`);
  w.document.close();
  w.focus();
  return w;
}

export default function RevenuePage() {
  const { rentals, rentalHistory, vehicles, groups, maintHist, companySettings, goto } = useApp();

  const [rangeKey, setRangeKey]       = useState('this-year');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [groupFilter, setGroupFilter]     = useState('');
  const [methodFilter, setMethodFilter]   = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [search, setSearch]               = useState('');
  const [txSort, setTxSort]               = useState({ key: 'start', dir: 'desc' });
  const [vehSort, setVehSort]             = useState({ key: 'total', dir: 'desc' });

  const range = useMemo(() => getRange(rangeKey, customRange), [rangeKey, customRange]);

  // ── Combined data: active/booked rentals + completed history ──────────────
  const allRentals = useMemo(() => [...rentals, ...rentalHistory], [rentals, rentalHistory]);

  // ── Filtered ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => allRentals.filter(r => {
    if (r.status === 'cancelled') return false;
    if (!inRange(r, range)) return false;
    if (vehicleFilter && r.vid !== vehicleFilter) return false;
    if (groupFilter) {
      const v = vehicles.find(x => x.id === r.vid);
      if (!v || v.groupId !== groupFilter) return false;
    }
    if (methodFilter  && r.payMethod !== methodFilter) return false;
    if (statusFilter  && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const v = vehicles.find(x => x.id === r.vid);
      if (!r.customer?.toLowerCase().includes(q) &&
          !r.id?.toLowerCase().includes(q) &&
          !(v?.name?.toLowerCase().includes(q))) return false;
    }
    return true;
  }), [allRentals, range, vehicleFilter, groupFilter, methodFilter, statusFilter, search, vehicles]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalRevenue = useMemo(() => filtered.reduce((s, r) => s + (r.agreedPrice || 0), 0), [filtered]);
  const collected    = useMemo(() => filtered.filter(r => r.status === 'complete').reduce((s, r) => s + (r.agreedPrice || 0), 0), [filtered]);
  const outstanding  = useMemo(() => filtered.filter(r => ['active','booked','overdue'].includes(r.status)).reduce((s, r) => s + (r.agreedPrice || 0), 0), [filtered]);
  const avgRental    = filtered.length ? totalRevenue / filtered.length : 0;
  const totalDaysRented = filtered.reduce((s, r) => s + (r.calSpan || 0), 0);

  const maintInRange = useMemo(() => maintHist.filter(m => {
    if (!m.cost) return false;
    if (!range || !m.date) return true;
    const d = new Date(m.date);
    return d >= range.from && d <= range.to;
  }), [maintHist, range]);
  const maintCost = maintInRange.reduce((s, m) => s + (m.cost || 0), 0);
  const netProfit = totalRevenue - maintCost;

  // ── Monthly bar chart ─────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const monthlyData = useMemo(() => {
    const data = Array(12).fill(0);
    filtered.forEach(r => {
      if (!r.start) return;
      const d = new Date(r.start);
      if (d.getFullYear() === currentYear) data[d.getMonth()] += r.agreedPrice || 0;
    });
    return data;
  }, [filtered, currentYear]);
  const maxBar = Math.max(...monthlyData, 1);

  // ── By vehicle (sortable table) ────────────────────────────────────────────
  const rangeDays = range ? Math.max(1, Math.round((range.to - range.from) / 86400000)) : 365;
  const byVehicle = useMemo(() => {
    const rows = vehicles.map(v => {
      const vr = filtered.filter(r => r.vid === v.id);
      const total = vr.reduce((s, r) => s + (r.agreedPrice || 0), 0);
      const days  = vr.reduce((s, r) => s + (r.calSpan || 0), 0);
      const avg   = vr.length ? total / vr.length : 0;
      const occ   = Math.min(100, Math.round((days / rangeDays) * 100));
      return { ...v, total, count: vr.length, days, avg, occ };
    }).filter(v => v.count > 0);

    const { key, dir } = vehSort;
    rows.sort((a, b) => {
      const av = a[key] ?? 0, bv = b[key] ?? 0;
      return dir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [filtered, vehicles, vehSort, rangeDays]);

  const mostRented = byVehicle[0];

  // ── By payment method ──────────────────────────────────────────────────────
  const byMethod = useMemo(() => {
    const m = {};
    filtered.forEach(r => { if (r.payMethod) m[r.payMethod] = (m[r.payMethod] || 0) + (r.agreedPrice || 0); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  // ── Busiest month ─────────────────────────────────────────────────────────
  const busiestIdx = monthlyData.indexOf(Math.max(...monthlyData));
  const busiestMonth = monthlyData[busiestIdx] > 0 ? MONTHS[busiestIdx] : '—';

  // ── Sorted transactions ────────────────────────────────────────────────────
  const sortedTx = useMemo(() => {
    const rows = [...filtered];
    const { key, dir } = txSort;
    rows.sort((a, b) => {
      let av, bv;
      if (key === 'start')    { av = new Date(a.start || 0); bv = new Date(b.start || 0); }
      else if (key === 'amount') { av = a.agreedPrice || 0; bv = b.agreedPrice || 0; }
      else if (key === 'customer') { av = a.customer || ''; bv = b.customer || ''; return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
      else { av = a[key] || ''; bv = b[key] || ''; return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av)); }
      return dir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [filtered, txSort]);

  function handleTxSort(key) {
    setTxSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  }
  function handleVehSort(key) {
    setVehSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  }

  const fmt = n => (n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Export helpers (period label + filename slug, then CSV / PDF) ──
  const PRESET_NAMES = { 'this-month': 'This Month', 'last-month': 'Last Month', 'this-year': 'This Year', 'all-time': 'All Time', 'custom': 'Custom' };
  const periodLabel = (() => {
    const name = PRESET_NAMES[rangeKey] || '';
    if (!range) return name || 'All Time';
    const f = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${name} · ${f(range.from)} → ${f(range.to)}`;
  })();
  const periodSlug = (() => {
    if (!range) return rangeKey === 'all-time' ? 'all-time' : new Date().toISOString().slice(0, 10);
    const s = (d) => { const p = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
    return `${s(range.from)}_to_${s(range.to)}`;
  })();
  const statsForExport = { totalRevenue, collected, outstanding, netProfit, avgRental, totalDaysRented, maintCost };

  function doExportCSV() {
    if (sortedTx.length === 0) return;
    exportRevenueCSV(sortedTx, vehicles, periodSlug);
  }
  function doExportPDF() {
    if (sortedTx.length === 0) return;
    printRevenuePDF({ rows: sortedTx, vehicles, byVehicle, byMethod, stats: statsForExport, cs: companySettings, periodLabel });
  }

  return (
    <div>
      <PageHeader
        title="Revenue"
        icon="📊"
        subtitle="Financial overview — revenue, transactions, and trends across your fleet."
      />

      {/* ── Filter bar ── */}
      <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', padding: '10px 14px', marginBottom: 14, boxShadow: 'var(--sh)', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['this-month','This Month'],['last-month','Last Month'],['this-year','This Year'],['all-time','All Time'],['custom','Custom']].map(([k,l]) => (
            <button key={k} onClick={() => setRangeKey(k)}
              className={rangeKey === k ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}>{l}</button>
          ))}
        </div>
        {rangeKey === 'custom' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="date" className="fi" value={customRange.from} onChange={e => setCustomRange(p => ({ ...p, from: e.target.value }))} style={{ fontSize: 11, padding: '3px 8px' }} />
            <span style={{ fontSize: 11, color: 'var(--tx3)' }}>→</span>
            <input type="date" className="fi" value={customRange.to} onChange={e => setCustomRange(p => ({ ...p, to: e.target.value }))} style={{ fontSize: 11, padding: '3px 8px' }} />
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="fsel" value={groupFilter} onChange={e => { setGroupFilter(e.target.value); setVehicleFilter(''); }}>
            <option value="">All groups</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select className="fsel" value={vehicleFilter} onChange={e => { setVehicleFilter(e.target.value); setGroupFilter(''); }}>
            <option value="">All vehicles</option>
            {(groupFilter
              ? vehicles.filter(v => v.groupId === groupFilter)
              : vehicles
            ).map(v => <option key={v.id} value={v.id}>{v.icon} {v.name.split('–')[0].trim()}</option>)}
          </select>
          <select className="fsel" value={methodFilter} onChange={e => setMethodFilter(e.target.value)}>
            <option value="">All methods</option>
            {Object.entries(METHOD_LABELS).map(([k,l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="fsel" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="booked">Booked</option>
            <option value="overdue">Overdue</option>
            <option value="complete">Complete</option>
          </select>
          <div className="srch" style={{ width: 170 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-sm" disabled={sortedTx.length === 0} onClick={doExportCSV}>⬇ CSV</button>
          <button className="btn btn-ghost btn-sm" disabled={sortedTx.length === 0} onClick={doExportPDF}>🖨 PDF</button>
        </div>
      </div>

      {/* ── Stat cards row 1 ── */}
      <div className="sg sg-4" style={{ marginBottom: 10 }}>
        <div className="sc gr">
          <div className="sc-lb">Total Revenue</div>
          <div className="sc-vl">€{fmt(totalRevenue)}</div>
          <div className="sc-ds">{filtered.length} rental{filtered.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="sc ac">
          <div className="sc-lb">Outstanding</div>
          <div className="sc-vl">€{fmt(outstanding)}</div>
          <div className="sc-ds">Active + booked</div>
        </div>
        <div className="sc am">
          <div className="sc-lb">Collected</div>
          <div className="sc-vl">€{fmt(collected)}</div>
          <div className="sc-ds">Completed rentals</div>
        </div>
        <div className="sc" style={{ borderLeft: `3px solid ${netProfit >= 0 ? 'var(--gr)' : 'var(--ac)'}` }}>
          <div className="sc-lb">Net Profit</div>
          <div className="sc-vl" style={{ color: netProfit >= 0 ? 'var(--gr)' : 'var(--ac)' }}>€{fmt(netProfit)}</div>
          <div className="sc-ds">Revenue − maintenance</div>
        </div>
      </div>

      {/* ── Stat cards row 2 ── */}
      <div className="sg sg-4" style={{ marginBottom: 14 }}>
        <div className="sc">
          <div className="sc-lb">Avg Rental Value</div>
          <div className="sc-vl">€{fmt(avgRental)}</div>
          <div className="sc-ds">Per contract</div>
        </div>
        <div className="sc">
          <div className="sc-lb">Total Days Rented</div>
          <div className="sc-vl">{totalDaysRented}</div>
          <div className="sc-ds">Across all vehicles</div>
        </div>
        <div className="sc">
          <div className="sc-lb">Top Earner</div>
          <div className="sc-vl" style={{ fontSize: 14 }}>{mostRented ? `${mostRented.icon} ${mostRented.name?.split('–')[0]?.trim()}` : '—'}</div>
          <div className="sc-ds">{mostRented ? `€${fmt(mostRented.total)}` : 'No data'}</div>
        </div>
        <div className="sc">
          <div className="sc-lb">Busiest Month</div>
          <div className="sc-vl">{busiestMonth}</div>
          <div className="sc-ds">{monthlyData[busiestIdx] > 0 ? `€${fmt(monthlyData[busiestIdx])}` : 'No data'}</div>
        </div>
      </div>

      {/* ── Monthly bar chart ── */}
      <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', padding: 16, boxShadow: 'var(--sh)', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12 }}>Monthly Revenue — {currentYear}</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140 }}>
          {MONTHS.map((m, i) => {
            const val   = monthlyData[i];
            const isNow = i === new Date().getMonth();
            const h     = val ? Math.max(8, (val / maxBar) * 118) : 4;
            return (
              <div key={m} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                {val > 0 && (
                  <div style={{ fontSize: 8, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace", whiteSpace: 'nowrap' }}>
                    €{val >= 1000 ? (val/1000).toFixed(1)+'k' : val}
                  </div>
                )}
                <div style={{
                  width: '100%', height: h,
                  background: val > 0 ? (isNow ? 'var(--ac)' : 'var(--bl)') : 'var(--bd)',
                  borderRadius: '3px 3px 0 0', opacity: val > 0 ? 1 : 0.35,
                  transition: 'height .3s',
                }} />
                <div style={{ fontSize: 8, color: isNow ? 'var(--ac)' : 'var(--tx3)', fontWeight: isNow ? 700 : 400 }}>{m}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Vehicle breakdown table + Payment methods ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 12, marginBottom: 12 }}>

        {/* Vehicle table */}
        <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', padding: 14, boxShadow: 'var(--sh)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Revenue by Vehicle</div>
          {byVehicle.length === 0
            ? <div style={{ fontSize: 11, color: 'var(--tx3)' }}>No data for this period</div>
            : (
              <div>
                {/* Header */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr .7fr .7fr 1fr .8fr .7fr', gap: 6, padding: '4px 0', borderBottom: '1px solid var(--bd)', marginBottom: 4 }}>
                  {[['name','Vehicle'],['count','Rentals'],['days','Days'],['total','Revenue'],['avg','Avg/Rental'],['occ','Occupancy']].map(([k,l]) => (
                    <SortTh key={k} label={l} sortKey={k} current={vehSort} onSort={handleVehSort} />
                  ))}
                </div>
                {byVehicle.map(v => (
                  <div key={v.id} style={{ display: 'grid', gridTemplateColumns: '2fr .7fr .7fr 1fr .8fr .7fr', gap: 6, padding: '5px 0', borderBottom: '1px solid var(--bd)', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14 }}>{v.icon}</span>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 500 }}>{v.name?.split('–')[0]?.trim()}</div>
                        <div style={{ fontSize: 9, color: 'var(--tx3)' }}>{v.plate}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, textAlign: 'center' }}>{v.count}</div>
                    <div style={{ fontSize: 11, textAlign: 'center' }}>{v.days}</div>
                    <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{fmt(v.total)}</div>
                    <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: 'var(--tx2)' }}>€{fmt(v.avg)}</div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ flex: 1, height: 4, background: 'var(--bd)', borderRadius: 2 }}>
                          <div style={{ height: '100%', width: `${v.occ}%`, background: v.occ > 70 ? 'var(--gr)' : v.occ > 40 ? 'var(--bl)' : 'var(--am)', borderRadius: 2 }} />
                        </div>
                        <span style={{ fontSize: 9, color: 'var(--tx3)', minWidth: 24 }}>{v.occ}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </div>

        {/* Payment methods */}
        <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', padding: 14, boxShadow: 'var(--sh)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Payment Methods</div>
          {byMethod.length === 0
            ? <div style={{ fontSize: 11, color: 'var(--tx3)' }}>No data for this period</div>
            : byMethod.map(([method, total]) => {
              const pct = Math.round((total / (totalRevenue || 1)) * 100);
              return (
                <div key={method} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11 }}>{METHOD_LABELS[method] || method}</span>
                    <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>
                      €{fmt(total)} <span style={{ color: 'var(--tx3)', fontWeight: 400 }}>({pct}%)</span>
                    </span>
                  </div>
                  <div style={{ height: 5, background: 'var(--bd)', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: 'var(--gr)', borderRadius: 3, transition: 'width .3s' }} />
                  </div>
                </div>
              );
            })}

          {/* Maintenance costs mini section */}
          {maintInRange.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 16, marginBottom: 8, paddingTop: 12, borderTop: '1px solid var(--bd)', display: 'flex', justifyContent: 'space-between' }}>
                <span>🔧 Maintenance</span>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 9, padding: '1px 6px' }} onClick={() => goto('maintenance')}>View →</button>
              </div>
              {maintInRange.slice(0, 4).map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '3px 0', borderBottom: '1px solid var(--bd)', color: 'var(--tx2)' }}>
                  <span>{m.icon} {m.veh} — {m.type}</span>
                  <span style={{ color: 'var(--am)', fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>−€{m.cost}</span>
                </div>
              ))}
              {maintInRange.length > 4 && (
                <div style={{ fontSize: 10, color: 'var(--tx3)', textAlign: 'center', marginTop: 4 }}>+{maintInRange.length - 4} more</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: 11, paddingTop: 6, marginTop: 2 }}>
                <span>Total</span>
                <span style={{ fontFamily: "'DM Mono',monospace", color: 'var(--am)' }}>−€{fmt(maintCost)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Transactions table ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>Transactions ({sortedTx.length})</div>
        <div style={{ fontSize: 11, color: 'var(--tx3)' }}>
          Total: <strong style={{ color: 'var(--tx)' }}>€{fmt(totalRevenue)}</strong>
          {outstanding > 0 && <span style={{ color: 'var(--am)', marginLeft: 8 }}>· Outstanding: €{fmt(outstanding)}</span>}
        </div>
      </div>

      <div className="tbl">
        <div className="tbl-hd" style={{ gridTemplateColumns: '.8fr 1.4fr 1.3fr 1fr .5fr .9fr .9fr .7fr' }}>
          <SortTh label="Contract"  sortKey="id"       current={txSort} onSort={handleTxSort} />
          <SortTh label="Vehicle"   sortKey="vid"      current={txSort} onSort={handleTxSort} />
          <SortTh label="Customer"  sortKey="customer" current={txSort} onSort={handleTxSort} />
          <SortTh label="Period"    sortKey="start"    current={txSort} onSort={handleTxSort} />
          <SortTh label="Days"      sortKey="calSpan"  current={txSort} onSort={handleTxSort} />
          <SortTh label="Amount"    sortKey="amount"   current={txSort} onSort={handleTxSort} />
          <div className="th">Method</div>
          <div className="th">Status</div>
        </div>

        {sortedTx.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--tx3)' }}>
            No transactions match your filters
          </div>
        )}

        {sortedTx.map(r => {
          const v = vehicles.find(x => x.id === r.vid);
          return (
            <div key={r.id} className="tr" style={{ gridTemplateColumns: '.8fr 1.4fr 1.3fr 1fr .5fr .9fr .9fr .7fr' }}>
              <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'var(--tx3)' }}>{r.id}</div>
              <div style={{ fontSize: 11 }}>{v?.icon} {v?.name?.split('–')[0]?.trim() || '—'}</div>
              <div style={{ fontSize: 11 }}>
                <div style={{ fontWeight: 500 }}>{r.customer}</div>
                {r.phone && <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{r.phone}</div>}
              </div>
              <div style={{ fontSize: 10, color: 'var(--tx2)' }}>
                <div>{r.startLabel || '—'}</div>
                {r.endLabel && <div style={{ color: 'var(--tx3)' }}>→ {r.endLabel}</div>}
              </div>
              <div style={{ fontSize: 11, textAlign: 'center', color: 'var(--tx2)' }}>{r.calSpan || '—'}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: 12 }}>
                €{fmt(r.agreedPrice || 0)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--tx2)' }}>{METHOD_LABELS[r.payMethod] || r.payMethod || '—'}</div>
              <div>
                <span style={{ fontSize: 10, fontWeight: 600, color: STATUS_COLORS[r.status] || 'var(--tx3)' }}>
                  {STATUS_LABELS[r.status] || r.status || '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
